


































import Vue from 'vue';
import { MStoreInputInlineUpdateModel } from '@/types';
import { MStoreInputInlineUpdateService } from '@/services/MStoreInputInlineUpdateService';
import { BaseModel } from '@/types/models/BaseModel';
import SnotifyConfiguration from '@/classes/SnotifyConfiguration';
import { ComponentHelper, ModalHelpers, DeviceHelper } from '@/mixins';
import { mapActions } from 'vuex';

export default Vue.extend({
  name: 'MStoreInputInlineUpdate',
  mixins: [ComponentHelper, ModalHelpers, DeviceHelper],
  data() {
    return {
      originalValue: '',
      value: '',
      inEditMode: false,
      submitting: false,
      inputHasFocus: false,
      actionButtonHasFocus: false
    };
  },
  props: {
    initialValue: {
      type: String,
      required: true
    },
    apiUrl: {
      type: String,
      required: true
    },
    maxLength: {
      type: Number,
      required: true
    },
    id: {
      type: String,
      required: true
    },
    label: {
      type: String,
      required: true
    },
    isDisabled: {
      type: Boolean,
      default: false
    },
    buttonTitle: {
      type: String
    },
    storeNamespace: {
      type: String
    },
    storeAction: {
      type: String
    }
  },
  methods: {
    ...mapActions({
      async _updateStore(this: any, dispatch, payload) {
        if (this.storeNamespace && this.storeAction) {
          return await dispatch(`${this.storeNamespace}/${this.storeAction}`, payload);
        }
      }
    }),
    updateStore(this: any, value: string) {
      this._updateStore(value);
    },
    async onActionButtonClick(this: any) {
      if (this.isDirty) {
        const success = await this.saveDataAsync();
        if (success) {
          this.originalValue = this.value;
          this.updateStore(this.value);
        } else {
          return;
        }
      }
      if (!this.inEditMode) {
        this.setFocusToInput();
      }
      this.inEditMode = !this.inEditMode;
    },
    setFocusToInput(this: any) {
      if (this.isIpad() || this.isIOS()) {
        return;
      }
      this.$nextTick(() => {
        this.focusElementById(this.id);
      });
    },
    onActionButtonBlur(this: any) {
      this.actionButtonHasFocus = false;
    },
    onActionButtonFocus(this: any) {
      this.actionButtonHasFocus = true;
    },
    onActionButtonMouseDown(this: any) {
      this.actionButtonHasFocus = true;
    },
    onInputTab(this: any) {
      this.actionButtonHasFocus = true;
    },
    onInputFocus(this: any) {
      this.inputHasFocus = true;
    },
    async onInputBlur(this: any) {
      this.inputHasFocus = false;
      if (!this.actionButtonHasFocus && this.inEditMode && !this.isDirty) {
        this.inEditMode = false;
      }
    },
    async saveDataAsync(this: any): Promise<boolean> {
      try {
        this.submitting = true;
        const result: BaseModel = await MStoreInputInlineUpdateService.updateDataAsync(this.apiUrl, {
          data: this.value
        } as MStoreInputInlineUpdateModel);
        if (result && !result.hasNotification) {
          return true;
        }
        if (result && result.hasNotification) {
          SnotifyConfiguration.showNotification(result.notification, result.presentationType);
        }
      } catch (error) {
        this.$log(error);
      } finally {
        this.submitting = false;
      }
      return false;
    },
    hasValidationErrors(this: any, flags: any, errors: string[]): boolean {
      return flags && (flags.dirty || flags.validated) && errors && errors.length > 0;
    },
    actionButtonIcon(this: any, flags: any, errors: string[]): string {
      if (this.isDirty && this.hasValidationErrors(flags, errors)) {
        return 'exclamation-triangle';
      }
      return this.validActionButtonIcon;
    },
    actionButtonDisabled(this: any, flags: any, errors: string[]): string {
      return (this.isDirty && this.hasValidationErrors(flags, errors)) || this.isDisabledAndViewMode;
    }
  },
  computed: {
    isDirty(this: any): boolean {
      return this.inEditMode && this.originalValue !== this.value;
    },
    validActionButtonIcon(this: any): string {
      return this.inEditMode && this.isDirty ? 'save' : this.inEditMode ? 'pen' : 'edit';
    },
    actionButtonVariant(this: any): string {
      return this.inEditMode && this.isDirty ? 'secondary' : 'primary';
    },
    validationRules(this: any) {
      const veeValidateRules = {} as any;
      veeValidateRules.max = this.maxLength;
      return veeValidateRules;
    },
    validationErrors(this: any) {
      return this.errors;
    },
    unsavedChangesMessage(this: any): string {
      return `Changes to ${this.label} are unsaved`;
    },
    isDisabledAndViewMode(this: any): boolean {
      return this.isDisabled && !this.inEditMode;
    }
  },
  watch: {
    isDirty(this: any, value) {
      this.$emit(`${this.id}-dirty`, value);
    },
    inEditMode(this: any, value) {
      this.$emit(`${this.id}-editing`, value);
    }
  },

  mounted(this: any) {
    this.originalValue = this.initialValue;
    this.value = this.initialValue;
  }
});
